import { importMarkDown, ZipTransformer } from '@blocksuite/affine/blocks';
import type { Doc, DocCollection } from '@blocksuite/affine/store';
import { Job } from '@blocksuite/affine/store';
import {
  type DocConverterControllerPostConvertBody,
  type FileConverterControllerPostConvertBody,
  getBaseUrl,
} from '@docnboard/rest';

import { getFileNameFromContentDisposition } from './content-disposition';
import { downloadBlob } from './download-blob';

const getFile = async (response: Response) => {
  if (!response.ok) {
    throw new Error(response.statusText);
  }

  const contentDisposition = response.headers.get('Content-Disposition');

  if (!contentDisposition) {
    throw new Error('Content-Disposition header is missing');
  }

  const filename = getFileNameFromContentDisposition(contentDisposition);

  if (!filename) {
    throw new Error('Filename is missing');
  }

  const buffer = await response.arrayBuffer();

  return {
    buffer,
    filename: filename,
    extension: filename.split('.').pop(),
  };
};

export class DocTransformer {
  static async exportPdf(doc: Doc) {
    const job = new Job({
      collection: doc.collection,
      docCRUD: {
        create: (id: string) => {
          return doc.collection.createDoc({ id });
        },
      },
    });
    const snapshot = await job.docToSnapshot(doc);
    if (!snapshot) {
      return;
    }

    if (!snapshot) {
      return;
    }

    const pageTitle = doc.meta?.title || 'Untitled';

    const optionsConverter: DocConverterControllerPostConvertBody = {
      type: 'dnb_to_pdf',
      workspaceId: doc.collection.id,
      docId: doc.id,
    };

    const { buffer, extension } = await fetch(
      getBaseUrl() + '/api/v2/services/converter/doc',
      {
        method: 'POST',
        body: JSON.stringify(optionsConverter),
        headers: {
          'Content-Type': 'application/json',
        },
      }
    ).then(getFile);

    if (buffer) {
      downloadBlob(new Blob([buffer]), `${pageTitle}.${extension}`);
    }
  }

  static async exportDocx(doc: Doc) {
    const job = new Job({
      collection: doc.collection,
      docCRUD: {
        create: (id: string) => {
          return doc.collection.createDoc({ id });
        },
      },
    });
    const snapshot = await job.docToSnapshot(doc);
    if (!snapshot) {
      return;
    }

    if (!snapshot) {
      return;
    }

    const pageTitle = doc.meta?.title || 'Untitled';
    const optionsConverter: DocConverterControllerPostConvertBody = {
      type: 'dnb_to_docx',
      workspaceId: doc.collection.id,
      docId: doc.id,
    };

    const { buffer, extension } = await fetch(
      getBaseUrl() + '/api/v2/services/converter/doc',
      {
        method: 'POST',
        body: JSON.stringify(optionsConverter),
        headers: {
          'Content-Type': 'application/json',
        },
      }
    ).then(getFile);

    if (buffer) {
      downloadBlob(new Blob([buffer]), `${pageTitle}.${extension}`);
    }
  }

  static async exportHtml(doc: Doc) {
    const job = new Job({
      collection: doc.collection,
      docCRUD: {
        create: (id: string) => {
          return doc.collection.createDoc({ id });
        },
      },
    });
    const snapshot = await job.docToSnapshot(doc);
    if (!snapshot) {
      return;
    }

    if (!snapshot) {
      return;
    }

    const pageTitle = doc.meta?.title || 'Untitled';

    const optionsConverter: DocConverterControllerPostConvertBody = {
      type: 'dnb_to_html',
      workspaceId: doc.collection.id,
      docId: doc.id,
    };

    const { buffer, extension } = await fetch(
      getBaseUrl() + '/api/v2/services/converter/doc',
      {
        method: 'POST',
        body: JSON.stringify(optionsConverter),
        headers: {
          'Content-Type': 'application/json',
        },
      }
    ).then(getFile);

    if (buffer) {
      downloadBlob(new Blob([buffer]), `${pageTitle}.${extension}`);
    }
  }

  static async exportMarkdown(doc: Doc) {
    const job = new Job({
      collection: doc.collection,
      docCRUD: {
        create: (id: string) => {
          return doc.collection.createDoc({ id });
        },
      },
    });
    const snapshot = await job.docToSnapshot(doc);
    if (!snapshot) {
      return;
    }

    if (!snapshot) {
      return;
    }

    const pageTitle = doc.meta?.title || 'Untitled';

    const optionsConverter: DocConverterControllerPostConvertBody = {
      type: 'dnb_to_md',
      workspaceId: doc.collection.id,
      docId: doc.id,
    };

    const { buffer, extension } = await fetch(
      getBaseUrl() + '/api/v2/services/converter/doc',
      {
        method: 'POST',
        body: JSON.stringify(optionsConverter),
        headers: {
          'Content-Type': 'application/json',
        },
      }
    ).then(getFile);

    if (buffer) {
      downloadBlob(new Blob([buffer]), `${pageTitle}.${extension}`);
    }
  }

  static async importDocx(docCollection: DocCollection, file: File) {
    const formData = new FormData();
    const optionsConverter: FileConverterControllerPostConvertBody = {
      type: 'docx_to_md',
      file,
    };

    formData.append('type', optionsConverter.type);
    formData.append('file', optionsConverter.file);

    const { extension, buffer } = await fetch(
      getBaseUrl() + '/api/v2/services/converter/file',
      {
        method: 'POST',
        body: formData,
      }
    ).then(getFile);

    const downloadedFile = new File(
      [new Blob([buffer])],
      `${file.name.split('.').slice(0, -1).join('.')}.${extension}`
    );
    return await importMarkDown({
      collection: docCollection,
      file: downloadedFile,
    });
  }
}

export class SnapshotTransformer {
  static async exportDoc(doc: Doc) {
    const collection = doc.collection;
    const blob = await ZipTransformer.exportDocs(collection, [doc]);
    const pageTitle = doc.meta?.title || 'Untitled';

    if (blob) {
      downloadBlob(blob, `${pageTitle}.zip`);
    }
  }
}
